import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { getAddressAudit } from "../api";
import { useEffect } from "react";

export function useDirectorAddressAudit(
  directorId: string,
  addressId: string,
  enabled = true,
) {
  const queryClient = useQueryClient();

  // Clear cache whenever enabled becomes true (sidebar opens)
  // or when addressId changes (address added/updated/deleted)
  // Use directorId as the cache key to ensure each director has their own cache
  useEffect(() => {
    if (enabled && directorId) {
      // Remove all cached queries for this director's address audit
      // Use exact: false to clear all variants including those with different addressId
      queryClient.removeQueries({
        queryKey: ["director-address-audit", directorId],
        exact: false,
      });
    }
  }, [enabled, directorId, addressId, queryClient]);

  // Optionally keep cleanup on unmount for good hygiene
  useEffect(() => {
    return () => {
      queryClient.removeQueries({
        queryKey: ["director-address-audit", directorId],
        exact: false,
      });
    };
  }, [queryClient, directorId]);

  return useInfiniteQuery({
    queryKey: ["director-address-audit", directorId, addressId],
    queryFn: async ({ pageParam = 1 }) => {
      const response = await getAddressAudit(
        addressId,
        pageParam as number,
        20,
      );
      return {
        data: response.data,
        pagination: response.pagination,
        nextPage:
          pageParam < response.pagination.totalPages
            ? (pageParam as number) + 1
            : undefined,
      };
    },
    getNextPageParam: (lastPage) => lastPage.nextPage,
    initialPageParam: 1,
    enabled: enabled && !!directorId,
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
    staleTime: 0,
    gcTime: 0,
  });
}
